import { PLAYER_CARD_LAYOUTS } from "@teamtable/api";
import { useCallback, useEffect, useMemo, useState } from "react";

const KNOWN_KEYS = [
  "app_url",
  "content_bundle_url",
  "chrome_fps_counter",
  "chrome_devtools",
  "screen_rotation",
  "screen_brightness",
  "adb_enabled",
  "player_card_layout_id",
] as const;

interface DeviceConfigEditorProps {
  gameSettingsEnabled?: boolean;
  configText: string;
  onConfigTextChange: (text: string) => void;
  onSave: () => void;
  saving: boolean;
  message: { type: "success" | "error"; text: string } | null;
  onMessageClear: () => void;
}

function parseConfig(text: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(text);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return null;
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}

function hasUnknownKeys(config: Record<string, unknown>): boolean {
  return Object.keys(config).some((k) => !(KNOWN_KEYS as readonly string[]).includes(k));
}

export function DeviceConfigEditor({
  gameSettingsEnabled = false,
  configText,
  onConfigTextChange,
  onSave,
  saving,
  message,
  onMessageClear,
}: DeviceConfigEditorProps) {
  const parsed = useMemo(() => parseConfig(configText), [configText]);
  const isValid = parsed !== null;

  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Auto-expand advanced section when there are unknown keys
  useEffect(() => {
    if (parsed && hasUnknownKeys(parsed)) {
      setAdvancedOpen(true);
    }
  }, []); // Only on mount

  const updateKey = useCallback(
    (key: string, value: unknown) => {
      if (!parsed) return;
      // An undefined value drops the key entirely: that is how a field returns
      // to its default, and Save turns the absence into an explicit delete.
      const updated = { ...parsed };
      if (value === undefined) {
        delete updated[key];
      } else {
        updated[key] = value;
      }
      onConfigTextChange(JSON.stringify(updated, null, 2));
      onMessageClear();
    },
    [parsed, onConfigTextChange, onMessageClear],
  );

  const getString = (key: string): string => {
    if (!parsed) return "";
    const val = parsed[key];
    return typeof val === "string" ? val : "";
  };

  const getBool = (key: string): boolean => {
    if (!parsed) return false;
    return parsed[key] === true;
  };

  const getNumber = (key: string): number | "" => {
    if (!parsed) return "";
    const val = parsed[key];
    return typeof val === "number" ? val : "";
  };

  const inputClass =
    "w-full px-3 py-2 rounded-md bg-surface border border-border text-text-primary focus:outline-none focus:border-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
  const labelClass = "block text-sm text-text-secondary mb-1";

  return (
    <div>
      {/* Structured Fields */}
      <div className="space-y-4 mb-4">
        {!isValid && (
          <div className="px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            Fix invalid JSON below to use these controls
          </div>
        )}

        <div>
          <label className={labelClass} htmlFor="player-card-layout">
            Default Card Placement
          </label>
          <select
            id="player-card-layout"
            className={inputClass}
            value={getString("player_card_layout_id")}
            disabled={!isValid || !gameSettingsEnabled}
            onChange={(e) => updateKey("player_card_layout_id", e.target.value || undefined)}
          >
            <option value="">Default (four sides)</option>
            {PLAYER_CARD_LAYOUTS.map((id) => (
              <option key={id} value={id}>
                {id.replace(/_/g, " ")}
              </option>
            ))}
          </select>
          {!gameSettingsEnabled && (
            <p className="text-xs text-text-muted mt-1">
              Card placement editing becomes available after the games update.
            </p>
          )}
        </div>
        {/* App URL */}
        <div>
          <label className={labelClass}>App URL</label>
          <input
            type="text"
            value={getString("app_url")}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "") {
                updateKey("app_url", undefined);
              } else {
                updateKey("app_url", val);
              }
            }}
            placeholder="https://app.main.teamtable.co"
            aria-label="App URL"
            disabled={!isValid}
            className={`${inputClass} font-mono text-sm`}
          />
          <p className="text-xs text-text-muted mt-1">
            Platform page URL that the device loads in Chrome. Leave empty and the device keeps the
            URL it was provisioned with — there is no platform-wide default.
          </p>
        </div>

        {/* Content Bundle URL */}
        <div id="content-bundle" className="scroll-mt-6">
          <label className={labelClass}>Content Bundle URL</label>
          <input
            type="text"
            value={getString("content_bundle_url")}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "") {
                updateKey("content_bundle_url", undefined);
              } else {
                updateKey("content_bundle_url", val);
              }
            }}
            placeholder="https://cdn.example.com/bundle.js"
            aria-label="Content Bundle URL"
            disabled={!isValid}
            className={`${inputClass} font-mono text-sm`}
          />
          <p className="text-xs text-text-muted mt-1">
            Override the default content bundle for this device. Leave empty and the device falls
            back to the platform content bundle. Appended as a query param to the App URL.
          </p>
        </div>

        {/* Boolean toggles row */}
        <div className="grid grid-cols-2 gap-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={getBool("chrome_fps_counter")}
              onChange={(e) => updateKey("chrome_fps_counter", e.target.checked)}
              disabled={!isValid}
              className="w-4 h-4 rounded border-border bg-surface text-accent focus:ring-accent focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <span className="text-sm text-text-secondary">Chrome FPS Counter</span>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={getBool("chrome_devtools")}
              onChange={(e) => updateKey("chrome_devtools", e.target.checked)}
              disabled={!isValid}
              className="w-4 h-4 rounded border-border bg-surface text-accent focus:ring-accent focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <span className="text-sm text-text-secondary">Chrome DevTools</span>
          </label>
        </div>

        {/* ADB enabled */}
        <div className="border border-yellow-500/30 bg-yellow-500/10 rounded-md p-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={getBool("adb_enabled")}
              onChange={(e) => updateKey("adb_enabled", e.target.checked)}
              disabled={!isValid}
              className="w-4 h-4 rounded border-border bg-surface text-accent focus:ring-accent focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <div>
              <span className="text-sm text-text-secondary">ADB Enabled</span>
              <p className="text-xs text-text-muted mt-0.5">
                Enables remote ADB access to the device.
              </p>
            </div>
          </label>
        </div>

        {/* Screen rotation + brightness row */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Screen Rotation</label>
            <select
              value={getNumber("screen_rotation")}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "") {
                  updateKey("screen_rotation", undefined);
                } else {
                  updateKey("screen_rotation", Number(val));
                }
              }}
              aria-label="Screen Rotation"
              disabled={!isValid}
              className={inputClass}
            >
              <option value="">Default</option>
              <option value="0">0°</option>
              <option value="180">180°</option>
            </select>
          </div>

          <div>
            <label className={labelClass}>Screen Brightness</label>
            <input
              type="number"
              value={getNumber("screen_brightness")}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "") {
                  updateKey("screen_brightness", undefined);
                } else {
                  updateKey("screen_brightness", Number(val));
                }
              }}
              // Clamping per keystroke makes the range unreachable: typing "80"
              // lands 8 → 20, then 200 → 100. Clamp once the field is left.
              onBlur={() => {
                const current = getNumber("screen_brightness");
                if (current === "") return;
                const clamped = Math.min(100, Math.max(20, current));
                if (clamped !== current) {
                  updateKey("screen_brightness", clamped);
                }
              }}
              min={20}
              max={100}
              placeholder="20-100"
              aria-label="Screen Brightness"
              disabled={!isValid}
              className={inputClass}
            />
          </div>
        </div>
      </div>

      {/* Advanced JSON */}
      <div className="border border-border rounded-md overflow-hidden">
        <button
          type="button"
          onClick={() => setAdvancedOpen(!advancedOpen)}
          className="w-full flex items-center justify-between px-3 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-surface transition-colors"
        >
          <span>Advanced (JSON)</span>
          <span className="text-xs">{advancedOpen ? "▼" : "▶"}</span>
        </button>
        {advancedOpen && (
          <div className="border-t border-border">
            <textarea
              value={configText}
              onChange={(e) => {
                onConfigTextChange(e.target.value);
                onMessageClear();
              }}
              rows={8}
              className="w-full px-3 py-2 bg-surface text-text-primary font-mono text-sm focus:outline-none focus:border-accent transition-colors resize-y border-0"
            />
          </div>
        )}
      </div>

      {/* Save button + message */}
      <div className="flex items-center gap-3 mt-3">
        <button
          onClick={onSave}
          disabled={saving || !isValid}
          className="px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? "Saving..." : "Save Config"}
        </button>
        {message && (
          <p
            className={`text-sm ${message.type === "success" ? "text-green-400" : "text-red-400"}`}
          >
            {message.text}
          </p>
        )}
      </div>
    </div>
  );
}
